import { useState } from 'react';
import { Badge, Text } from '@shopify/polaris';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';
import { getAreasSorted, AREAS, getLineLabel } from '../../../../config/roasterySpec';

const TYPE_STYLES = {
  added: { bg: '#e8f5e9', badge: 'success' },
  removed: { bg: '#fce4ec', badge: 'critical' },
  changed: { bg: '#fff8e1', badge: 'warning' }
};

const deltaColor = (val) => (val > 0.005 ? '#27ae60' : val < -0.005 ? '#e74c3c' : '#666');
const deltaPrefix = (val) => (val > 0.005 ? '+' : '');

const FIELD_LABELS = {
  Quantity: 'Qty',
  UnitPurchasePrice: 'Cost Price',
  UnitSellingPrice: 'Sell Price',
  IsOptional: 'Optional',
  DiscountPercent: 'Discount %',
  Description: 'Description',
  ProductID: 'Product'
};

function formatFieldValue(field, value) {
  if (value == null) return '(none)';
  if (field === 'UnitPurchasePrice' || field === 'UnitSellingPrice') return formatToCurrency(value);
  if (field === 'IsOptional') return value ? 'Yes' : 'No';
  if (field === 'DiscountPercent') return `${value}%`;
  return String(value);
}

function ChangeRow({ change }) {
  const [expanded, setExpanded] = useState(false);
  const line = change.lineB || change.lineA;
  const style = TYPE_STYLES[change.type];
  const desc = line.Description || line.Product?.ProductNameNL || line.Product?.ProductName || '(no description)';
  const position = `${line.AreaCode} > ${String(line.LineNumber).padStart(3, '0')}`;
  const hasFieldChanges = change.type === 'changed' && change.changes;

  return (
    <div style={{ backgroundColor: style.bg, borderRadius: '6px', padding: '8px 10px', marginBottom: '4px' }}>
      <div onClick={() => hasFieldChanges && setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: hasFieldChanges ? 'pointer' : 'default' }}>
        {hasFieldChanges && <span style={{ fontSize: '10px', color: '#666', width: '10px' }}>{expanded ? '\u25BC' : '\u25B6'}</span>}
        <Badge tone={style.badge} size="small">
          {change.type}
        </Badge>
        <Text variant="bodySm" as="span" tone="subdued">
          {position}
        </Text>
        <Text variant="bodySm" as="span" fontWeight="semibold" truncate>
          {desc}
        </Text>
        <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          <Text variant="bodySm" as="span" fontWeight="semibold">
            <span style={{ color: deltaColor(change._impact.sellingDelta) }}>
              {deltaPrefix(change._impact.sellingDelta)}
              {formatToCurrency(change._impact.sellingDelta)}
            </span>
          </Text>
        </span>
      </div>

      {expanded && change.changes && (
        <div style={{ marginTop: '6px', paddingLeft: '26px' }}>
          {Object.entries(change.changes).map(([field, { from, to }]) => (
            <div key={field} style={{ fontSize: '12px', color: '#555', padding: '1px 0' }}>
              <span style={{ fontWeight: 600 }}>{FIELD_LABELS[field] || field}: </span>
              <span style={{ textDecoration: 'line-through', color: '#999' }}>{formatFieldValue(field, from)}</span>
              <span style={{ margin: '0 4px' }}>&rarr;</span>
              <span>{formatFieldValue(field, to)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DiffLineChanges({ changes }) {
  const [filter, setFilter] = useState('all');

  if (!changes || changes.length === 0) return null;

  const counts = { all: changes.length, added: 0, removed: 0, changed: 0 };
  for (const c of changes) counts[c.type]++;

  const filters = [
    { key: 'all', label: `All (${counts.all})` },
    { key: 'added', label: `Added (${counts.added})` },
    { key: 'removed', label: `Removed (${counts.removed})` },
    { key: 'changed', label: `Changed (${counts.changed})` }
  ];

  const filtered = filter === 'all' ? changes : changes.filter((c) => c.type === filter);

  // Group by area
  const areaOrder = getAreasSorted().map((a) => a.code);
  const grouped = {};
  for (const c of filtered) {
    const line = c.lineB || c.lineA;
    const code = line.AreaCode;
    if (!grouped[code]) grouped[code] = [];
    grouped[code].push(c);
  }
  const orderedAreas = areaOrder.filter((c) => grouped[c]);
  const extraAreas = Object.keys(grouped).filter((c) => !orderedAreas.includes(c));

  return (
    <div>
      <Text variant="headingSm" as="h3" fontWeight="semibold">
        Line Changes
      </Text>

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '6px', marginTop: '8px', marginBottom: '10px' }}>
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '4px 10px',
              borderRadius: '12px',
              border: '1px solid #ccc',
              backgroundColor: filter === f.key ? '#333' : '#fff',
              color: filter === f.key ? '#fff' : '#333',
              fontSize: '12px',
              cursor: 'pointer',
              fontWeight: filter === f.key ? 600 : 400
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Scrollable list */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {[...orderedAreas, ...extraAreas].map((code) => (
          <div key={code} style={{ marginBottom: '10px' }}>
            <Text variant="bodySm" as="p" fontWeight="semibold" tone="subdued">
              {AREAS[code]?.label || code}
            </Text>
            <div style={{ marginTop: '4px' }}>
              {grouped[code].map((c, i) => (
                <ChangeRow key={i} change={c} />
              ))}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <Text variant="bodySm" as="p" tone="subdued">
            No changes match the selected filter.
          </Text>
        )}
      </div>
    </div>
  );
}
